import { AppBar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Toolbar, } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { formatNumber } from '../utils/numbersFormatter'
import { useTranslation } from 'react-i18next'
import { ClientCashflow, ClientDeal } from '../redux/reducers/clients'
import { NewLayout } from '../components/newLayout/NewLayout'
import { newThemeObject } from '../newTheme'
import { OrderDetailDialog } from '../components/dialogs/OrderDetailDialog'
import { FileImportDialog } from '../components/dialogs/FileImportDialog'
import CardWrapper from '../components/common/CardWrapper'
import { YearQuickFilter } from '../components/common/YearQuickFilter'
import classNames from 'classnames'
import { useSelector } from '../redux/hooks/useSelector'
import { LoadingIndicator } from '../components/common/LoadingIndicator'
import { useClientApi } from '../hooks/useClientApi'
import { getClientIdFromUser, isClientUser } from '../services/keycloak.service'
import { DatePicker, LocalizationProvider } from '@mui/lab'
import AdapterDateFns from '@date-io/date-fns'
import { tokens } from '../styles/tokens'
import DownloadIcon from '@mui/icons-material/DownloadRounded'
import { ColoredIconButton } from '../components/common/ColoredIconButton'
import { exportToXls } from '../utils/exportToXls'
import useMobileWidth from '../hooks/useMobileWidth'

const CURRENT_YEAR = new Date().getFullYear()
const QUICK_FILTER_YEARS = [CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR]

type SortField = 'portfolio' | 'amount' | 'currency' | 'tradeDate' | 'valueDate' | 'text' | 'tpid'

// Radi hodnoty ruznych typu (retezce, cisla, datumy) pro razeni sloupcu tabulky pohybu na peneznich uctech.
const compareValues = (a: any, b: any) => {
  if (a instanceof Date && b instanceof Date) {
    const at = a.getTime()
    const bt = b.getTime()
    if (isNaN(at) && isNaN(bt)) return 0
    if (isNaN(at)) return -1
    if (isNaN(bt)) return 1
    return at - bt
  }
  if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b)
  return a > b ? 1 : a < b ? -1 : 0
}

const getSortValue = (row: ClientCashflow, field: SortField) => {
  switch (field) {
    case 'tradeDate':
    case 'valueDate':
      return new Date(row[field] as any)
    default:
      return row[field]
  }
}

const useStyles = makeStyles(theme => ({
  paper: {
    overflowY: 'auto',
    overflowX: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  sectionHeader: {
    background: `linear-gradient(135deg, ${tokens.color.primary[800]}, ${tokens.color.primary[600]})`,
    margin: '-16px -16px 16px',
    padding: '12px 16px',
    borderRadius: `${tokens.radius.lg} ${tokens.radius.lg} 0 0`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    minHeight: '48px',
  },
  sectionHeaderTitle: {
    color: tokens.color.white,
    fontSize: '0.9375rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  table: {
    minWidth: 1000,
  },
  tableHead: {
    ...newThemeObject.table.tableHead,
  },
  tableHeadChildrenText: {
    ...newThemeObject.table.tableHeadChildrenText,
  },
  tableHeadChildren: {
    ...newThemeObject.table.tableHeadChildren,
  },
  tableHeadChildrenFirst: {
    ...newThemeObject.table.tableHeadChildrenFirst,
  },
  tableHeadChildrenLast: {
    ...newThemeObject.table.tableHeadChildrenLast,
  },
  tableContent: {
    ...newThemeObject.table.tableContent,
  },
  tableContentFirst: {
    ...newThemeObject.table.tableContentFirst,
  },
  firstClickableRow: {
    cursor: 'pointer',
    transition: `background-color ${tokens.transition.fast}`,
    '& .MuiTableCell-root': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: tokens.color.primary[50],
    },
  },
  lastRow: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
  tableRow: {
    transition: `background-color ${tokens.transition.fast}`,
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      borderTop: 'thin solid',
      borderColor: tokens.color.neutral[200],
    },
    '&:hover': {
      backgroundColor: tokens.color.primary[50],
    },
  },
  text: {
    fontWeight: '400',
    margin: '0',
    fontSize: '0.7rem',
    textWrap: 'wrap',
  },

  tableTopBar: {
    backgroundColor: tokens.color.white,
    border: `1px solid ${tokens.color.neutral[200]}`,
    boxShadow: 'none',
    borderRadius: tokens.radius.lg,
    marginBottom: theme.spacing(2),
  },
  tableToolBar: {
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    alignContent: 'space-around',
    width: '100%',
    padding: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    margin: 0,
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  textField: {
    width: '100%',
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      marginTop: 0,
      marginRight: theme.spacing(1.5),
      maxWidth: '180px',
    },
  },
  yearButtons: {
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      marginTop: 0,
      marginRight: theme.spacing(1.5),
    },
  },
  filler: {
    flexGrow: 1,
  },
  icon: {
    color: newThemeObject.palette.main.sectext,
    backgroundColor: '#ffffff',
  },
  // Karty pohybu pro mobil - nahrazuji sirokou tabulku, aby se nemuselo nic posouvat do stran.
  mobileList: {
    display: 'flex',
    flexDirection: 'column',
  },
  mobileCard: {
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${tokens.color.neutral[200]}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  mobileCardGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: theme.spacing(0.75, 1.5),
  },
  mobileCardField: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  mobileCardLabel: {
    margin: 0,
    fontSize: '0.65rem',
    color: tokens.color.neutral[500],
  },
  mobileCardValue: {
    margin: '2px 0 0',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: tokens.color.neutral[900],
    overflowWrap: 'break-word',
  },
}))

export const ClientDetailCashflow = () => {
  const classes = useStyles()
  const isMobile = useMobileWidth()

  const loading = useSelector(state => state.clients.loading)
  const client = useSelector(state => state.clients.client)
  const clientCashflowsTmp = useSelector(state => state.clients.clientCashflows)

  const { getClientCashflow } = useClientApi()
  const { getClient } = useClientApi()
  const { clientId } = useParams<{ clientId: string }>()
  const { t } = useTranslation()

  const getClientId = () => {
    if (isClientUser()) {
      return getClientIdFromUser()
    } else {
      return clientId
    }
  }
  
  const clientId2 = getClientId()
  useEffect(()=>{
    if ( !client ) {
      getClient(Number(clientId2))
    }
    if ( client && client.clientId!==0 ) {
      getClientCashflow(client.clientId, '', '');
    }
  }, [client])

  const [dateFrom, setDateFrom] = useState<Date>();
  const [dateTo, setDateTo] = useState<Date>();
  const [search, setSearch] = useState<string>();
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  const [clientCashflow, setClientCashflow] = useState<ClientCashflow[]>([]);

  const [sortField, setSortField] = useState<SortField | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const onDateFromChange = (name: string) => (date: Date | null) => {
    setDateFrom(date || undefined)
    setSelectedYear(null)
  }

  const onDateToChange = (name: string) => (date: Date | null) => {
    setDateTo(date || new Date())
    setSelectedYear(null)
  }

  const onOrderTypeSearchChange = (event: React.ChangeEvent<{ value: string }>) => {
    setSearch(event.target.value)
  }

  const onYearSelect = (year: number) => {
    const now = new Date()
    const isCurrentYear = year === now.getFullYear()
    setDateFrom(new Date(year, 0, 1))
    setDateTo(isCurrentYear ? now : new Date(year, 11, 31, 23, 59, 59, 999))
    setSelectedYear(year)
  }

  useEffect(()=>{
    if ( clientCashflowsTmp ) {
      if ( clientCashflowsTmp.length === 0 ) {
        setClientCashflow([]);
        return
      }
      
      let tmp = [...clientCashflowsTmp];

      if ( dateFrom ) {
        tmp = tmp.filter((item)=>(new Date(item.valueDate)>dateFrom))
      }
      if ( dateTo ) {
        tmp = tmp.filter((item)=>(new Date(item.valueDate)<dateTo))
      }
      if ( search ) {
        tmp = tmp.filter((item)=>(
          // ( item.firstName && item.firstName.toUpperCase().includes(search.toUpperCase()) )
          // || ( item.surname && item.surname.toUpperCase().includes(search.toUpperCase()) )
          ( item.text && item.text.toUpperCase().includes(search.toUpperCase()) )
          || ( item.tpid && item.tpid.toUpperCase().includes(search.toUpperCase()) )
          || ( item.currency && item.currency.toUpperCase().includes(search.toUpperCase()) )
        ));
      }
      setClientCashflow(tmp);
    }
  }, [clientCashflowsTmp, dateFrom, dateTo, search])

  const sortedClientCashflow = [...clientCashflow]
  if (sortField) {
    sortedClientCashflow.sort((a, b) => {
      const result = compareValues(getSortValue(a, sortField), getSortValue(b, sortField))
      return sortDirection === 'asc' ? result : -result
    })
  }

  const handleExport = () => {
    const headers = [
      t('FIRST_NAME'), t('SURNAME'), t('PORTFOLIO'), t('AMOUNT'), t('CURRENCY'), t('TRADE_DATE'), t('VALUE_DATE'), t('TEXT'), t('DESCRIPTION'),
    ]
    const rows = sortedClientCashflow.map(clientDeal => [
      clientDeal.firstName, clientDeal.surname, clientDeal.portfolio, formatNumber(clientDeal.amount),
      clientDeal.currency, clientDeal.tradeDate, clientDeal.valueDate, clientDeal.text, clientDeal.tpid,
    ])
    exportToXls('pohyby_penezni_ucty.xls', headers, rows)
  }

  return (
    <NewLayout pageName="TRANSACTIONS">

      <AppBar position="static" className={classes.tableTopBar} elevation={0}>
      <Toolbar variant="dense" className={classes.tableToolBar}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            renderInput={params => <TextField {...params} variant="standard" className={classes.textField} label={t('DATE_FROM2')}/>}
            inputFormat="dd/MM/yyyy"
            value={dateFrom || null}
            onChange={onDateFromChange('dateFromId')}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            renderInput={params => (
              <TextField {...params} variant="standard" className={classes.textField} label={t('DATE_TO')} />
            )}
            inputFormat="dd/MM/yyyy"
            value={dateTo || null}
            onChange={onDateToChange('dateToId')}
          />
        </LocalizationProvider>

        <YearQuickFilter
          className={classes.yearButtons}
          years={QUICK_FILTER_YEARS}
          selectedYear={selectedYear}
          onSelectYear={onYearSelect}
        />

        <TextField
          variant="standard"
          className={classes.textField}
          placeholder={t('SEARCH')}
          label={t('SEARCH')}
          onChange={onOrderTypeSearchChange}
          value={search}
        />

        <span className={classes.filler} />

        <ColoredIconButton
          className={classes.icon}
          onClick={handleExport}
          size="small"
          icon={<DownloadIcon />}
          title={t('EXPORT_XLS')}
        />
      </Toolbar>
      </AppBar>

      {loading && <LoadingIndicator />}
      {!loading && 
        <TableContainer component={CardWrapper} className={classes.paper} key={66}>
              <div className={classes.sectionHeader}>
                <p className={classes.sectionHeaderTitle}>{t("CASHFLOW_TRANSACTION")}</p>
              </div>
              {isMobile ? (
                <div className={classes.mobileList}>
                  {sortedClientCashflow.map((clientDeal: ClientCashflow, index: number) => (
                    <div className={classes.mobileCard} key={index}>
                      <div className={classes.mobileCardGrid}>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('FIRST_NAME')}</p>
                          <p className={classes.mobileCardValue}>{clientDeal.firstName}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('SURNAME')}</p>
                          <p className={classes.mobileCardValue}>{clientDeal.surname}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('PORTFOLIO')}</p>
                          <p className={classes.mobileCardValue}>{clientDeal.portfolio}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('AMOUNT')}</p>
                          <p className={classes.mobileCardValue}>{formatNumber(clientDeal.amount)}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('CURRENCY')}</p>
                          <p className={classes.mobileCardValue}>{clientDeal.currency}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('TRADE_DATE')}</p>
                          <p className={classes.mobileCardValue}>{clientDeal.tradeDate}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('VALUE_DATE')}</p>
                          <p className={classes.mobileCardValue}>{clientDeal.valueDate}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('TEXT')}</p>
                          <p className={classes.mobileCardValue}>{clientDeal.text}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('DESCRIPTION')}</p>
                          <p className={classes.mobileCardValue}>{clientDeal.tpid}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
              <Table className={classes.table} size="small">
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenFirst])}>
                      <p className={classes.tableHeadChildrenText}>{t('FIRST_NAME')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('SURNAME')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <TableSortLabel
                        className={classes.tableHeadChildrenText}
                        active={sortField === 'portfolio'}
                        direction={sortField === 'portfolio' ? sortDirection : 'asc'}
                        onClick={() => handleSort('portfolio')}
                      >
                        {t('PORTFOLIO')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <TableSortLabel
                        className={classes.tableHeadChildrenText}
                        active={sortField === 'amount'}
                        direction={sortField === 'amount' ? sortDirection : 'asc'}
                        onClick={() => handleSort('amount')}
                      >
                        {t('AMOUNT')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <TableSortLabel
                        className={classes.tableHeadChildrenText}
                        active={sortField === 'currency'}
                        direction={sortField === 'currency' ? sortDirection : 'asc'}
                        onClick={() => handleSort('currency')}
                      >
                        {t('CURRENCY')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <TableSortLabel
                        className={classes.tableHeadChildrenText}
                        active={sortField === 'tradeDate'}
                        direction={sortField === 'tradeDate' ? sortDirection : 'asc'}
                        onClick={() => handleSort('tradeDate')}
                      >
                        {t('TRADE_DATE')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <TableSortLabel
                        className={classes.tableHeadChildrenText}
                        active={sortField === 'valueDate'}
                        direction={sortField === 'valueDate' ? sortDirection : 'asc'}
                        onClick={() => handleSort('valueDate')}
                      >
                        {t('VALUE_DATE')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <TableSortLabel
                        className={classes.tableHeadChildrenText}
                        active={sortField === 'text'}
                        direction={sortField === 'text' ? sortDirection : 'asc'}
                        onClick={() => handleSort('text')}
                      >
                        {t('TEXT')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <TableSortLabel
                        className={classes.tableHeadChildrenText}
                        active={sortField === 'tpid'}
                        direction={sortField === 'tpid' ? sortDirection : 'asc'}
                        onClick={() => handleSort('tpid')}
                      >
                        {t('DESCRIPTION')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenLast])} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedClientCashflow.map((clientDeal: ClientCashflow, index: number) => (
                    <React.Fragment key={index}>
                      <TableRow
                        className={classNames(index === 0 ? classes.firstClickableRow : classes.tableRow)}
                      >
                        <TableCell className={classes.tableContentFirst}>
                          <p className={classes.text}>{clientDeal.firstName}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{clientDeal.surname}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{clientDeal.portfolio}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(clientDeal.amount)}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{clientDeal.currency}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{clientDeal.tradeDate}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{clientDeal.valueDate}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{clientDeal.text}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{clientDeal.tpid}</p>
                        </TableCell>

                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
              )}
        </TableContainer>
      }

      <OrderDetailDialog />
      <FileImportDialog />
    </NewLayout>
  )
}
